import { initializeApp } from 'firebase/app';
import {
  OAuthProvider,
  GoogleAuthProvider,
  FacebookAuthProvider,
  RecaptchaVerifier,
  ConfirmationResult,
  signInWithPhoneNumber,
  signInWithPopup,
  signOut,
  getAuth,
} from 'firebase/auth';
import { languageProvider } from './languageProvider';

export class FirebaseProvider {
  private recaptcha: RecaptchaVerifier | null = null;

  private phoneConfirmationResult: ConfirmationResult | null = null;

  private googleProvider: any;

  private facebookProvider: any;

  private appleProvider: any;

  private firebaseConfig: { [key: string]: string | undefined } = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  };

  constructor() {
    initializeApp(this.firebaseConfig);
    const auth = getAuth();
    auth.languageCode = languageProvider.getLanguageKey();
    this.googleProvider = new GoogleAuthProvider();
    this.googleProvider.addScope('https://www.googleapis.com/auth/userinfo.email');
    this.googleProvider.setCustomParameters({ prompt: 'select_account' });
    this.facebookProvider = new FacebookAuthProvider();
    this.appleProvider = new OAuthProvider('apple.com');
    this.appleProvider.addScope('email');
    this.appleProvider.addScope('name');
  }

  googleAuth = async () => {
    try {
      const auth = getAuth();
      const result = await signInWithPopup(auth, this.googleProvider);
      if (!result.user) throw Error('[FirebaseProvider Google]: User Not Found');

      return result.user.getIdToken();
    } catch (e) {
      console.log(e);
      return null;
    }
  };

  initRecaptha = async (btnId: string) => {
    const auth = await getAuth();
    this.recaptcha = await new RecaptchaVerifier(
      btnId,
      { size: 'invisible' },
      auth,
    );
  };

  phoneAuth = async (phoneNumber: string) => {
    if (!this.recaptcha) throw Error('[FirebaseProvider]: Recaptha is not initialized');
    if (!phoneNumber) throw Error('[FirebaseProvider]: phoneNumber is empty');
    const auth = getAuth();

    try {
      if (!this.recaptcha) throw Error('[FirebaseProvider]: Recaptha is not initialized');
      this.phoneConfirmationResult = await signInWithPhoneNumber(auth, phoneNumber, this.recaptcha);
    } catch (e) {
      this.phoneConfirmationResult = null;
      throw e;
    }
  };

  confirmPhoneAuth = async (smsCode: string) => {
    try {
      if (!this.phoneConfirmationResult) throw Error('[FirebaseProvider Phone]: phoneConfirmationResult Not Found');
      const result = await this.phoneConfirmationResult.confirm(smsCode);
      if (!result.user) throw Error('[FirebaseProvider Phone]: User Not Found');

      return result.user.getIdToken();
    } catch (e) {
      console.log(e);
      throw e;
    }
  };

  facebookAuth = async () => {
    try {
      const auth = getAuth();
      const result = await signInWithPopup(auth, this.facebookProvider);
      if (!result.user) throw Error('[FirebaseProvider Facebook]: User Not Found');

      return result.user.getIdToken();
    } catch (e) {
      console.log(e);
      return null;
    }
  };

  appleAuth = async () => {
    try {
      const auth = getAuth();
      const result = await signInWithPopup(auth, this.appleProvider);
      if (!result.user) throw Error('[FirebaseProvider Apple]: User Not Found');

      return result.user.getIdToken();
    } catch (e) {
      console.log(e);
      return null;
    }
  };

  singOut = async () => {
    try {
      const auth = getAuth();
      return signOut(auth);
    } catch (e) {
      console.log(e);
      return e;
    }
  };
}
export const firebaseProvider = new FirebaseProvider();
